/**
 * MeetingRecorder — TASK-665
 *
 * Replaces the 21-file / 3,753-line LiveCockpit with a deliberately silent recorder.
 *
 * The product rule this encodes: **an RM sitting across from a client must not be reading
 * a live transcript.** The cockpit showed one anyway — along with an insight rail fed by
 * WebSocket events the backend stopped emitting when TASK-427 deleted the live analysis
 * agents. There is nothing to read here on purpose. The only job of this screen is to
 * prove capture is working: who, how long, is audio arriving, and how to stop.
 *
 * Everything the meeting produces (tasks, life events, FINSA notes, summary, KYC staging,
 * DNA delta) lands after the call via the post-meeting batch, and is reviewed on
 * `/meeting/:id/review`.
 *
 * Accessibility: every control is a real button with an accessible name, status changes
 * are announced via a polite live region, and no state is conveyed by colour alone —
 * each status carries an icon and a text label.
 */

import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Loader2,
  Mic,
  MicOff,
  Pause,
  Play,
  Radio,
  Square,
} from 'lucide-react'
import { toast } from 'sonner'

import AudioLevelMeter from './AudioLevelMeter'
import { useMeetingCapture } from '../../hooks/useMeetingCapture'
import { httpClient } from '../../lib/httpClient'
import { getMeetingDetails } from '../../services/api/meetingApi'
import { useAccount } from '../../contexts/AccountContext'

interface MeetingRecorderProps {
  sessionId?: string
  clientId?: string
  clientName?: string
  advisorId?: string
  meetingType?: string
  /** Conference URL — presence means the bot owns the audio and the mic stays off. */
  meetingUrl?: string
  onEndMeeting: () => void
  /** TASK-673: render inside a canvas tile (no full-screen wrapper, no back-to-dashboard
   *  link — the WidgetContainer owns the frame). The whole capture flow stays on the canvas. */
  embedded?: boolean
}

const MEETING_TYPE_LABELS: Record<string, string> = {
  advisory: 'Advisory',
  review: 'Portfolio Review',
  consultation: 'Consultation',
  onboarding: 'Onboarding',
}

function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const MeetingRecorder: React.FC<MeetingRecorderProps> = ({
  sessionId,
  clientId,
  clientName,
  advisorId,
  meetingType,
  meetingUrl,
  onEndMeeting,
  embedded = false,
}) => {
  const navigate = useNavigate()
  const { account, user } = useAccount()

  const [consentConfirmed, setConsentConfirmed] = useState(false)
  const [consentPending, setConsentPending] = useState(false)
  const [ending, setEnding] = useState(false)

  const capture = useMeetingCapture({
    sessionId,
    clientId,
    advisorId,
    accountId: account?.id,
    userId: user?.id,
    userRole: user?.role,
    meetingUrl,
  })

  const {
    status,
    mediaStream,
    suppression,
    botState,
    elapsedSeconds,
    chunksUploaded,
    failedChunks,
    hasPendingUploads,
    errorMessage,
  } = capture

  const isRecording = status === 'recording'
  const isPaused = status === 'paused'
  const botOwnsAudio = suppression === 'bot-owns-audio'

  // --------------------------------------------------------------------------
  // Interrupted capture (TASK-665 review)
  //
  // A refresh, a closed tab or a crashed browser leaves the session `live` with real
  // audio in `utterances` and NO end call — so no batch is ever enqueued. The recorder
  // used to remount into a pristine pre-consent screen, which never claimed a clean end
  // but also never told the RM that a recording was left open with saved audio in it.
  //
  // No new endpoint: `GET /meeting/{id}/details` already returns `status` and the derived
  // `capture_source` (from `utterances.source`). `capture_source === 'unknown'` means the
  // LOOKUP failed, not that nothing was captured, so it deliberately does not count — a
  // failed check must not be rendered as "you have audio waiting".
  // --------------------------------------------------------------------------
  const details = useQuery({
    queryKey: ['meeting-details', sessionId],
    queryFn: () => getMeetingDetails(sessionId as string),
    enabled: !!sessionId,
    staleTime: 30_000,
    retry: false,
  })

  const capturedBefore =
    !!details.data?.capture_source &&
    !['none', 'unknown'].includes(details.data.capture_source)
  const wasInterrupted =
    !consentConfirmed &&
    status === 'idle' &&
    !botOwnsAudio &&
    capturedBefore &&
    ['live', 'in_progress'].includes(details.data?.status ?? '')

  // --------------------------------------------------------------------------
  // Start — consent is recorded BEFORE the microphone is touched
  // --------------------------------------------------------------------------
  const handleStart = useCallback(async () => {
    if (!sessionId) return
    setConsentPending(true)
    try {
      // Durable, auditable record of the consent decision (actor + timestamp). If this
      // write fails we do NOT start recording — a recording with no consent record is
      // exactly what the business rule forbids.
      await httpClient.post(`/ws/live-meeting/consent/${sessionId}`, {
        consent_confirmed: true,
        client_id: clientId,
      })
    } catch {
      setConsentPending(false)
      toast.error('Could not record the consent confirmation. Recording was not started.')
      return
    }
    setConsentPending(false)
    setConsentConfirmed(true)
    await capture.start()
  }, [sessionId, clientId, capture])

  const handleEnd = useCallback(async () => {
    setEnding(true)
    const { ok, partial } = await capture.end()
    setEnding(false)

    if (!ok) {
      // Do not navigate away from an unresolved end — the RM would lose the retry.
      toast.error('The meeting could not be closed cleanly. Try ending again.')
      return
    }
    if (partial) {
      toast.warning(
        'Meeting ended, but some audio was not confirmed as uploaded. The record may be incomplete.'
      )
    } else {
      toast.success('Meeting ended. Analysis is running.')
    }
    onEndMeeting()
  }, [capture, onEndMeeting])

  // --------------------------------------------------------------------------
  // Status descriptor — icon + text + colour, never colour alone
  // --------------------------------------------------------------------------
  const statusDescriptor = (): { icon: React.ReactNode; label: string; tone: string } => {
    if (botOwnsAudio) {
      return {
        icon: <Radio className="h-4 w-4" aria-hidden="true" />,
        label:
          botState === 'in-call'
            ? 'The meeting bot is capturing this meeting'
            : 'A meeting bot will capture this meeting',
        tone: 'text-blue-700 bg-blue-50 border-blue-200',
      }
    }
    if (status === 'error') {
      return {
        icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
        label: errorMessage ?? 'Recording problem',
        tone: 'text-red-700 bg-red-50 border-red-200',
      }
    }
    if (isRecording) {
      return {
        icon: <Circle className="h-4 w-4 fill-current" aria-hidden="true" />,
        label: 'Recording',
        tone: 'text-green-700 bg-green-50 border-green-200',
      }
    }
    if (isPaused) {
      return {
        icon: <Pause className="h-4 w-4" aria-hidden="true" />,
        label: 'Paused — not recording',
        tone: 'text-amber-700 bg-amber-50 border-amber-200',
      }
    }
    if (status === 'ended') {
      return {
        icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
        label: 'Recording finished',
        tone: 'text-gray-700 bg-gray-50 border-gray-200',
      }
    }
    if (wasInterrupted) {
      return {
        icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
        label: 'Interrupted — this meeting was never closed',
        tone: 'text-amber-700 bg-amber-50 border-amber-200',
      }
    }
    return {
      icon: <MicOff className="h-4 w-4" aria-hidden="true" />,
      label: 'Not recording',
      tone: 'text-gray-700 bg-gray-50 border-gray-200',
    }
  }

  const descriptor = statusDescriptor()

  return (
    <div
      className={
        embedded
          ? 'flex w-full justify-center'
          : 'flex min-h-screen items-center justify-center bg-gray-50 p-6'
      }
    >
      <div
        className={
          embedded
            ? 'w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6'
            : 'w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'
        }
      >
        {/* Who + what */}
        <header className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            {clientName || 'Client meeting'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {meetingType ? MEETING_TYPE_LABELS[meetingType] ?? meetingType : 'Meeting'}
          </p>
        </header>

        {/* Status — announced to assistive tech on every change */}
        <div
          role="status"
          aria-live="polite"
          className={`mb-6 flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${descriptor.tone}`}
        >
          {descriptor.icon}
          <span>{descriptor.label}</span>
        </div>

        {/* Elapsed + level meter: the entire visual payload */}
        <div className="mb-8 flex flex-col items-center gap-4">
          <div
            className="font-mono text-4xl tabular-nums text-gray-900"
            aria-label={`Elapsed recording time ${formatElapsed(elapsedSeconds)}`}
          >
            {formatElapsed(elapsedSeconds)}
          </div>
          {isRecording && mediaStream ? (
            <AudioLevelMeter mediaStream={mediaStream} width={220} height={36} />
          ) : (
            <div className="h-9" aria-hidden="true" />
          )}
        </div>

        {/* Controls */}
        {botOwnsAudio ? (
          <p className="text-center text-sm text-gray-600">
            Your microphone stays off so the meeting is not recorded twice. You can close
            this screen — the bot keeps capturing.
          </p>
        ) : !consentConfirmed ? (
          <div className="space-y-4">
            {wasInterrupted && (
              <div
                data-testid="recorder-interrupted"
                role="status"
                className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              >
                <p className="font-medium">This meeting was interrupted.</p>
                <p className="mt-1 text-xs">
                  Audio captured before the interruption was saved, but the meeting was
                  never closed, so no analysis has run. Close it now to analyse what was
                  captured, or confirm consent again to keep recording.
                </p>
                <button
                  type="button"
                  onClick={handleEnd}
                  disabled={ending}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-amber-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-800 disabled:opacity-50"
                >
                  {ending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Square className="h-4 w-4" aria-hidden="true" />
                  )}
                  Close meeting and analyse what was captured
                </button>
              </div>
            )}
            <p className="text-sm text-gray-700">
              Confirm that you have the client&apos;s agreement to record this meeting.
              Recording will not start until you do.
            </p>
            <button
              type="button"
              onClick={handleStart}
              disabled={consentPending || !sessionId}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-kielis-navy px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-kielis-navy/90 disabled:opacity-50"
            >
              {consentPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Mic className="h-4 w-4" aria-hidden="true" />
              )}
              Consent confirmed — start recording
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={isPaused ? capture.resume : capture.pause}
              disabled={ending || status === 'error'}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              {isPaused ? (
                <>
                  <Play className="h-4 w-4" aria-hidden="true" /> Resume
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4" aria-hidden="true" /> Pause
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleEnd}
              disabled={ending}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              {ending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Square className="h-4 w-4" aria-hidden="true" />
              )}
              End meeting
            </button>
          </div>
        )}

        {/* Capture health — only shown when there is something honest to report. */}
        {(failedChunks > 0 || hasPendingUploads) && (
          <p className="mt-4 text-center text-xs text-gray-500" role="status" aria-live="polite">
            {failedChunks > 0 && (
              <span className="text-amber-700">
                {failedChunks} audio segment{failedChunks === 1 ? '' : 's'} failed to
                upload — the record may be incomplete.{' '}
              </span>
            )}
            {hasPendingUploads && <span>Uploading audio…</span>}
          </p>
        )}
        {chunksUploaded > 0 && failedChunks === 0 && !hasPendingUploads && (
          <p className="mt-4 text-center text-xs text-gray-400">
            {chunksUploaded} audio segment{chunksUploaded === 1 ? '' : 's'} saved
          </p>
        )}

        {!embedded && (
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-6 w-full text-center text-xs text-gray-400 underline hover:text-gray-600"
          >
            Back to dashboard
          </button>
        )}
      </div>
    </div>
  )
}

export default MeetingRecorder
